import { useState } from "react";
import { CtaButton, Folio, Line, d } from "./Chapters";
import { CONTACT_EMAIL } from "./Chapters";
import stageHq from "@/assets/damian-keynote-stage-hq.jpg.asset.json";
import hall from "@/assets/keynote-audience-hall.jpg.asset.json";
import couple from "@/assets/damian-portrait-teal.jpg.asset.json";
import bimmLogo from "@/assets/bimm-logo-white.png.asset.json";

/* 02 — Founder: BIMM identity, co-founder statement, two-beat narrative */
export const FounderProfile = () => (
  <section id="founder" className="ed-dark ed-section fp">
    <div className="ed-wrap">
      <Folio n="02" label="Founder" />
      <div className="ed-grid fp-grid">
        <div className="fp-head">
          <img data-reveal src={bimmLogo.url} alt="BIMM" className="fp-logo" loading="lazy" width={352} height={150} />
          <h2 data-lines data-fill className="ed-title fp-title" aria-label="Co-founder. From the ground up.">
            <Line i={0}>Co-founder.</Line>
            <Line i={1}>From the ground up.</Line>
          </h2>
          <p data-reveal style={d(120)} className="fp-sub">From a living room to BIMM University.</p>
        </div>
        <ol className="fp-seq">
          <li data-reveal>
            <span className="fp-age" aria-hidden>23</span>
            <p className="ed-lead">At 23, I co-founded the Brighton Institute of Modern Music with three partners, starting in my living room. We built its foundations and expanded it across multiple campuses. I drove student recruitment, sales and the commercial growth behind that expansion.</p>
          </li>
          <li data-reveal style={d(120)}>
            <span className="fp-age" aria-hidden>30</span>
            <p className="ed-lead">I sold my stake in BIMM at 30. The institution we built has since become BIMM University, spanning music, film, performing arts and other creative disciplines.</p>
          </li>
        </ol>
      </div>
    </div>
  </section>
);

const LEDGER = [
  { tag: "DK Music Business Academy", text: "An international online education platform helping independent musicians navigate the modern music industry and develop their careers." },
  { tag: "YouTube", text: "Practical music industry knowledge, shared with an international audience of artists, musicians and entrepreneurs." },
  { tag: "DK Artists", text: "Artist development and music marketing: content strategy, digital advertising and audience growth that help artists reach new listeners and build fanbases." },
  { tag: "Artist Management & Live Agency", text: "Businesses founded and sold, with experience on both the creative and commercial sides of the industry." },
];

const YT_CHANNEL = "https://www.youtube.com/@DamianKeyes";
const BIMM_FIGS = [
  { value: "$250M", what: "Valuation" },
  { value: "9,000+", what: "Students annually" },
];
const BIMM_NOTE = "BIMM's valuation and student figures reflect its continued growth after my departure.";
const SUPPORT = [
  { biz: "DK Artists", value: "1 Billion+", what: "Views generated for artists", line: "Through our music marketing and artist-development agency." },
  { biz: "YouTube", value: "200,000+", what: "Subscribers", line: "A global audience built through music industry education and artist growth.", link: YT_CHANNEL },
  { biz: "DK Music Business Academy", value: "83+", what: "Countries", line: "Countries in which our courses have been sold." },
];

/* 04 — Current businesses + results */
export const ProofChapter = () => (
  <section id="businesses" className="pf">
    <div className="ed-section pf-story">
      <div className="ed-wrap">
        <Folio n="04" label="Businesses & results" />
        <h2 data-lines className="ed-title pf-title" aria-label="Building audiences. Growing businesses. Developing artists.">
          <Line i={0}>Building audiences.</Line>
          <Line i={1}>Growing businesses.</Line>
          <Line i={2}>Developing artists.</Line>
        </h2>
        <div className="pf-diptych">
          <figure data-img className="pf-stage">
            <img src={stageHq.url} alt="Damian Keyes speaking on stage at a business conference" loading="lazy" width={1920} height={1371} />
          </figure>
          <div className="pf-side">
            <p data-reveal style={d(140)} className="pf-panel">Following BIMM, I continued building businesses across music education, artist management, marketing and the creative industries.</p>
            <figure data-img style={d(320)} className="pf-hall">
              <img src={hall.url} alt="A full conference hall watching Damian's keynote" loading="lazy" width={1600} height={1200} />
            </figure>
          </div>
        </div>
        <ol className="pf-index">
          {LEDGER.map((l, i) => (
            <li key={l.tag} data-reveal style={d(i * 60)}>
              <span className="pf-n" aria-hidden>{String(i + 1).padStart(2, "0")}</span>
              <h3 className="pf-tag">{l.tag}</h3>
              <p className="ed-body ed-muted">{l.text}</p>
            </li>
          ))}
        </ol>
      </div>
    </div>

    <div id="achievements" className="ed-dark ed-section rs">
      <div className="ed-wrap">
        <h2 data-lines className="ed-title rs-title" aria-label="A track record built on results.">
          <Line i={0}>A track record</Line>
          <Line i={1}>built on results.</Line>
        </h2>

        <section className="ed-grid rs-bimm" aria-label="BIMM">
          <header data-reveal className="rs-id">
            <img src={bimmLogo.url} alt="BIMM" className="rs-logo" width={352} height={150} loading="lazy" />
            <p className="rs-role">Co-founded at 23</p>
          </header>
          <div className="rs-bimm-figs">
            {BIMM_FIGS.map((f, i) => (
              <div key={f.value} data-reveal style={d(i * 100)} className="rs-fig">
                <p className="rs-num">{f.value}</p>
                <p className="rs-what">{f.what}</p>
              </div>
            ))}
            <p data-reveal style={d(200)} className="rs-note">{BIMM_NOTE}</p>
          </div>
        </section>

        <section className="rs-academy" aria-label="DK Music Business Academy">
          <p data-reveal className="rs-biz">DK Music Business Academy</p>
          <p data-reveal data-fill className="rs-hero"><span>30,000+</span></p>
          <p data-reveal style={d(120)} className="rs-academy-line">30,000+ artists around the world have joined DK Music Business Academy, making it the largest online music industry education platform in the world.</p>
        </section>

        <ol className="rs-support" aria-label="Supporting results">
          {SUPPORT.map((f, i) => (
            <li key={f.value} data-reveal style={d(260 + i * 120)}>
              <p className="rs-biz">{f.biz}</p>
              <p className="rs-num rs-num-sm">{f.value}</p>
              <p className="rs-what">{f.what}</p>
              <p className="rs-line">{f.line}</p>
              {f.link && <a className="ed-inline rs-link" href={f.link} target="_blank" rel="noopener noreferrer">Visit the YouTube channel <span aria-hidden>&rarr;</span></a>}
            </li>
          ))}
        </ol>
      </div>
    </div>
  </section>
);

const CopyEmail = () => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="ed-email"
      onClick={async () => {
        try { await navigator.clipboard.writeText(CONTACT_EMAIL); setCopied(true); setTimeout(() => setCopied(false), 1800); }
        catch { window.location.href = `mailto:${CONTACT_EMAIL}`; }
      }}
    >
      {CONTACT_EMAIL} <span className="ed-copied" aria-live="polite">{copied ? "Copied" : "Click to copy"}</span>
    </button>
  );
};

/* 05 — Contact and footer */
export const ContactFooter = () => (
  <footer id="contact" className="ed-section ct">
    <div className="ed-wrap">
      <Folio n="05" label="Contact" />
      <div className="ed-grid ct-grid">
        <div className="ct-copy">
          <div className="ct-intro">
            <h2 data-lines className="ed-display" aria-label="Let's build something exceptional.">
              <Line i={0}>Let's build</Line>
              <Line i={1}>something exceptional.</Line>
            </h2>
            <p data-reveal className="ed-lead mt-10">I'm interested in working with ambitious investors, educational institutions, record labels and industry leaders who are building the future of education and the creative industries.</p>
          </div>
          <p data-reveal style={d(80)} className="ct-lead ed-body ed-muted mt-8 max-w-[52ch]">Whether you're establishing a new institution, looking to grow student enrolments, developing creative talent or exploring a strategic partnership, I'd welcome a conversation.</p>
          <div data-reveal style={d(200)} className="ct-actions">
            <CtaButton />
            <CopyEmail />
          </div>
        </div>
        <figure data-img className="ct-photo">
          <img src={couple.url} alt="Portrait of Damian Keyes" loading="lazy" width={1538} height={1920} />
        </figure>
      </div>
      <div className="ed-grid ct-foot">
        <p className="ed-label">International Consulting · Institutional Growth · Music Industry Advisory · Strategic Partnerships</p>
        <div>
          <p className="ed-label ed-muted">For Independent Artists</p>
          <p className="ed-caption ed-muted mt-3 max-w-[52ch]">
            Explore <a className="ed-inline" href="https://www.dk-mba.com" target="_blank" rel="noopener noreferrer">DK Music Business Academy</a> and <a className="ed-inline" href="https://www.dk-artists.com" target="_blank" rel="noopener noreferrer">DK Artists</a> for music education, artist development and marketing services.
          </p>
        </div>
      </div>
    </div>
  </footer>
);
